import { useMutation } from '@apollo/client'
import { loadStripe } from '@stripe/stripe-js'
import classNames from 'classnames'
import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Badge,
  BadgeSize,
  BadgeType,
  Button,
  ButtonColor,
  NotificationType,
  TextArea,
  TooltipInfo,
} from '../../../components'
import env from '../../../config/env'
import { CREATE_SECOND_EDITS_REQUEST_MESSAGES } from '../../../gql'
import {
  CampaignAddon,
  Collaboration,
  CollaborationAddon,
  CountryCode,
  CreatorQuality,
  VideoDuration,
} from '../../../gql/types'
import { usePricing } from '../../../hooks'
import { useCreateAddonsCheckout } from '../../../hooks/payments'
import {
  formatPriceInCents,
  formattedAddonsPrice,
  getVideoPricing,
} from '../../../utils/PricingUtil'
import { UrlBuilder } from '../../../utils/UrlBuilder'
import { useNotification } from '../../../utils/hooks'
import { mapCampaignAddonsTypeToTextSimple } from '../../../utils/mappers'
import { CAN_PURCHASE_ADDONS_THRESHOLD_DAYS } from '../../../utils/constants'

const stripePromise = loadStripe(env.STRIPE_KEY)

export const mapSecondEditsVersionAddonToTextForBrand = (
  addons: CampaignAddon[],
) => {
  const addon: CampaignAddon = addons.find(
    (addon) => addon === CampaignAddon.secondEditedVersion,
  )

  // if purchased already
  if (addon) {
    return `You have purchased the ${mapCampaignAddonsTypeToTextSimple(addon)} add-on.`
  }

  // if not purchased
  return 'This add-on makes minor changes to the second video provided (the first edit one), like adjusting fonts and other small details. Submit all edit requests in one message.'
}

type EditSecondEditsAddonFormData = {
  quality: CreatorQuality
  secondEditedMessage: string | ''
}

interface IProps {
  collaboration: Collaboration
  addon: CollaborationAddon
  open: boolean
  setOpen: (event: any) => void
}

export const OrderSecondEditsVersionAddon: React.FC<IProps> = ({
  collaboration,
  addon,
  open,
  setOpen,
}) => {
  const campaign = collaboration.campaign
  const { brand } = campaign
  const { addNotification } = useNotification()
  const { pricing } = usePricing()
  const [creditPayment, setCreditPayment] = useState(false)
  const [initiateCheckoutLoading, setInitiateCheckoutLoading] = useState(false)
  const { createAddonsCheckout, loading } = useCreateAddonsCheckout()
  const cancelButtonRef = useRef(null)

  const [
    createSecondEditsRequestMessage,
    { loading: createMessageLoading, error },
  ] = useMutation(CREATE_SECOND_EDITS_REQUEST_MESSAGES)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<EditSecondEditsAddonFormData>({
    defaultValues: {
      quality: campaign.quality ?? CreatorQuality.regular,
      secondEditedMessage: collaboration?.secondEditsRequestMessage ?? '',
    },
  })

  const initiateCheckout = async ({ secondEditedMessage }) => {
    setInitiateCheckoutLoading(true)

    try {
      await createSecondEditsRequestMessage({
        variables: {
          collaborationId: collaboration.id,
          input: {
            secondEditsRequestMessage: secondEditedMessage,
          },
        },
      })

      const { data } = await createAddonsCheckout({
        variables: {
          brandId: brand.id,
          input: {
            addons: [CampaignAddon.secondEditedVersion],
            collaborationId: collaboration.id,
            successRedirectUrlPath: UrlBuilder.buildCampaignCollaboratorsUrl(
              brand.id,
              collaboration.campaign.id,
            ),
            cancelRedirectUrlPath: UrlBuilder.buildCampaignCollaboratorsUrl(
              brand.id,
              collaboration.campaign.id,
            ),
          },
        },
      })

      // if no stripe session it means the purchase was made with credits
      if (!data?.createAddonsCheckout?.stripeSessionId) {
        addNotification(
          `Your add-on order has been confirmed.`,
          NotificationType.success,
        )
      }

      // if stripe session id present - redirect to stripe
      if (data?.createAddonsCheckout?.stripeSessionId) {
        // if stripe session id present - redirect to stripe
        const stripe = await stripePromise

        // When the customer clicks on the button, redirect them to Checkout.
        const result = await stripe.redirectToCheckout({
          sessionId: data?.createAddonsCheckout?.stripeSessionId,
        })

        if (result.error) {
          // If `redirectToCheckout` fails due to a browser or network
          // error, display the localized error message to your customer
          // using `result.error.message`.
          addNotification(
            `Something went wrong. Please try again or contact support at hello@useclip.com.`,
            NotificationType.error,
          )
        }
      }

      setOpen(false)
      reset()
    } catch (error) {
      addNotification(
        // `Something went wrong. Please try again or contact support at hello@useclip.com.`,
        error.message,
        NotificationType.error,
      )
    }

    setInitiateCheckoutLoading(false)
  }

  const renderAddonsPrice = () => {
    const formattedPrice = formattedAddonsPrice({
      addons: CampaignAddon.secondEditedVersion,
      currency: campaign.currency,
      duration: campaign.videoDuration ?? VideoDuration.half,
      pricing,
    })

    return (
      <div className='border rounded-lg shadow-sm px-3 py-3 flex items-center'>
        <div className='flex flex-row justify-between items-center text-sm w-full'>
          <div className='font-medium text-gray-500 ml-1'>
            {formattedPrice}
            {brand.countryCode === CountryCode.GB && <span>{` `}+ VAT</span>}
          </div>
          <div>
            <Badge
              type={BadgeType.indigo}
              iconPosition='left'
              text={
                <TooltipInfo
                  valueKey='addons-badge'
                  content={`1 add-on`}
                  tooltipText={mapSecondEditsVersionAddonToTextForBrand(
                    collaboration.addons.map((addon) => addon.description),
                  )}
                />
              }
              size={BadgeSize.small}
              className='!mr-0'
            />
          </div>
        </div>
      </div>
    )
  }

  const renderPayWithCreditInput = () => {
    if (campaign.currency !== brand.currency) {
      return null
    }

    if (brand.creditCents <= 0) {
      return null
    }

    const formattedCredit = formatPriceInCents(
      {
        priceInCents: brand.creditCents,
        currency: campaign.currency,
      },
      { minimumFractionDigits: 0 },
    )

    return (
      <div className='relative flex items-start mt-3'>
        <div className='flex h-6 items-center'>
          <input
            id='payWithCredit'
            aria-describedby='payWithCredit-description'
            name='payWithCredit'
            type='checkbox'
            // checked={creditPayment}
            checked={true}
            disabled
            onChange={() => setCreditPayment((prev) => !prev)}
            className={classNames(
              'h-4 w-4 rounded border-gray-300 text-gray-600 disabled:accent-gray-300 focus:ring-gray-500 cursor-pointer',
              creditPayment
                ? 'focus:ring-gray-800 accent-primary-800'
                : 'accent-gray-300',
            )}
          />
        </div>
        <div className='ml-3 text-sm leading-6'>
          <label
            htmlFor='payWithCredit'
            className='font-medium text-gray-900 cursor-pointer'
          >
            Use credits
          </label>
          <label
            htmlFor='payWithCredit'
            id='payWithCredit-description'
            className='block leading-4 text-gray-500 cursor-pointer'
          >
            You have {formattedCredit} remaining credits. Credits will
            automatically be applied.
          </label>
        </div>
      </div>
    )
  }

  const videoPricing = getVideoPricing({
    currency: collaboration.campaign.currency,
    videoDuration: collaboration.campaign.videoDuration,
    pricing,
  })

  if (!addon && !collaboration.canPurchaseAddons) {
    return (
      <>
        <div className='bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 h-[480px] overflow-y-auto'>
          <div className='sm:flex sm:items-start'>
            <div className='mt-3 sm:mt-0 sm:ml-0 sm:text-left'>
              <h3 className='text-lg leading-6 font-medium text-gray-900'>
                Buy a second round of edits
              </h3>
              <div className='p-2 -mx-2'>
                <p className='text-sm text-gray-500'>
                  Add-ons are no longer available for purchase. The{' '}
                  {CAN_PURCHASE_ADDONS_THRESHOLD_DAYS}-day period after content
                  acceptance has expired.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className='bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse'>
          <Button
            colour={ButtonColor.primary}
            title='Confirm your order'
            type='submit'
            loading={initiateCheckoutLoading}
            disabled={true}
            className='w-full sm:w-auto'
          />
          <button
            type='button'
            className='mt-3 mr-3 w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-1 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
            onClick={() => setOpen(false)}
            ref={cancelButtonRef}
          >
            Cancel
          </button>
        </div>
      </>
    )
  }

  return (
    <form onSubmit={handleSubmit(initiateCheckout)}>
      <div className='bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 h-[480px] overflow-y-auto'>
        <div className='sm:flex sm:items-start'>
          <div className='mt-3 sm:mt-0 sm:ml-0 sm:text-left'>
            <h3 className='text-lg leading-6 font-medium text-gray-900'>
              Buy a second round of edits
            </h3>
            <div className='my-2'>
              <p className='text-sm text-gray-500'>
                {!addon && (
                  <>
                    A second edit version includes minor changes to the second
                    video provided (the first edit version), like adjusting
                    fonts and other small details. Submit all edit requests in
                    one message.
                  </>
                )}
              </p>
              {addon && (
                <p className='text-sm text-gray-500'>
                  You have already purchased a second edit add-on for this
                  collaboration.
                </p>
              )}
              {!addon && collaboration.canPurchaseAddons && (
                <p className='text-sm text-gray-500 mt-2'>
                  Add-ons available for purchase only{' '}
                  {collaboration.remainingDaysForAddonPurchase} more days.
                </p>
              )}
            </div>
            <div className='p-2 -mx-2'>
              <div className='mt-2 min-w-0 flex-1'>
                <TextArea
                  label='Add your edit request'
                  placeholder='Type to add your message'
                  name='secondEditedMessage'
                  minRows={5}
                  error={errors.secondEditedMessage?.message}
                  {...register('secondEditedMessage', {
                    required: `Please add a message.`,
                    maxLength: {
                      value: 10000,
                      message: `Message too long.`,
                    },
                  })}
                  disabled={createMessageLoading || !!addon}
                />
              </div>
            </div>
            {addon && (
              <div className='px-3 pt-[99px] flex justify-start items-center'></div>
            )}
            {!addon && (
              <>
                <div className='w-full items-center'>{renderAddonsPrice()}</div>
                {renderPayWithCreditInput()}
              </>
            )}
          </div>
        </div>
      </div>
      <div className='bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse'>
        <Button
          colour={ButtonColor.primary}
          title='Confirm your order'
          type='submit'
          loading={initiateCheckoutLoading}
          disabled={!!addon}
          className='w-full sm:w-auto'
        />
        <button
          type='button'
          className='mt-3 mr-3 w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-1 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
          onClick={() => setOpen(false)}
          ref={cancelButtonRef}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
